/**
 * Cashflow & Age Pension — the projection layer.
 *
 * WHAT THIS MODULE IS FOR (owner, 2026-08-28). Every other module in the app projects AROUND an
 * event: a move into care, a village entry, a downsize, a granny flat. This one projects a client
 * who does none of those things — they stay where they are, and the only thing that changes is what
 * they spend and how their money is arranged.
 *
 * It exists because of the Commonwealth Home Support Program. CHSP has NO MEANS TEST, so unlike
 * Support at Home there is no contribution for us to calculate — the provider sets the fee and the
 * client is told what it is. That inverts the usual job: the care cost stops being an OUTPUT we
 * compute and becomes an INPUT the client gives us. What we can then answer is the question they
 * actually have, which was never "what does CHSP cost" (they get a bill) but "can I afford this,
 * for how long, and is there anything I can do to be better off?"
 *
 * DELIBERATELY GENERAL, NARROWLY EXPOSED. Nothing here knows about CHSP, Support at Home, or how the
 * client arrived. The cost is just a recurring expense with a label. That is the owner's positioning
 * call: build it general so it can be opened from anywhere later, but for now reach it through one
 * door — the Support at Home overview — while the product decides how broad it wants to be.
 *
 * HOW A "NO EVENT" PROJECTION IS EXPRESSED, and why it is not a hack. `careStartDate` is set beyond
 * the horizon. The engine already treats that as a supported state: `careStartYearFor` returns 0,
 * documented as "never, within this run", and `careFractionOfPeriod` returns 0 for every period, so
 * no care fee is charged and none of the entry clocks (the RAD retention window, the former-home
 * exemption) ever start. Verified empirically before this module was written — a 10-year run charges
 * no care costs and pays the Age Pension on ordinary at-home rules.
 */
import { estateOf } from '../../../../shared/src/calculation/support/estateCgt.ts';
import * as calculationApi from '../../services/calculationApi.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { debtPayoffKey } from '../../../../shared/src/calculation/support/debtPayoff.ts';
import { annuityQuoteShapeOf, type AnnuityQuoteShape } from '../../strategy/annuityQuote.ts';
import type { GuaranteedIncomeGoals } from '../../components/strategy/GuaranteedIncomeQuestions.tsx';
import { allocateNonConcessionalContribution } from '../../../../shared/src/calculation/nonConcessionalContributions.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import { assetDisplayName } from '../../utils/assetDisplayName.ts';
import { cardPaymentStyleOf, isCreditCard, liabilityInterestRate } from '../../../../shared/src/calculation/support/creditCards.ts';

/**
 * A recurring cost the client tells us about — CHSP fees, a cleaner, private help at home.
 *
 * NOT a fact-find field, by owner direction. It lives in module state the way the Downsizer holds a
 * sale price, so no schema change is needed and the figure stays visibly the CLIENT'S rather than
 * ours. It is only persisted if they save a strategy.
 */
export interface SupportCostInput {
    /** Annual amount in dollars. */
    annual: number;
    /** What it is, in the client's words — shown back to them so the figure keeps its provenance. */
    label: string;
}

/**
 * One line of an engine breakdown.
 *
 * The engine emits these as ORDERED ARRAYS, not keyed objects — a working you read top to bottom,
 * with `--- Asset Test ---` style separator rows and values that are sometimes already-formatted
 * strings ("$45,000", "Part Pension") and sometimes raw numbers. Reading them as a keyed object was
 * my first mistake here: every lookup missed and the card would have rendered a column of zeros
 * with all the authority of a calculation.
 */
export interface BreakdownRow {
    label: string;
    value: number | string;
    emphasis?: boolean;
    /** Marks a row that EXPLAINS the figures without being one of them — never sum it. */
    note?: boolean;
}

/** One projected year, as this module reads it. Keys are the engine's own. */
export interface CashflowYear {
    year: number;
    /** Age Pension for the period. */
    socialSecurity: number;
    socialSecurityBreakdown?: BreakdownRow[];
    cashFlow: number;
    cashFlowBreakdown?: BreakdownRow[];
    netWorth: number;
    netWorthBreakdown?: BreakdownRow[];
    livingExpenses: number;
    investmentIncome: number;
    abpIncome?: number;
    rentalIncome?: number;
    tax: number;
    taxBreakdown?: BreakdownRow[];
    /** Set where the plan cannot be funded from income and drawdown — the runway signal. */
    unfundedShortfall?: number;
    fyLabel?: string;
    [key: string]: unknown;
}

export interface RunOptions {
    legacyYears?: number;
    /** Added to living expenses for the run. Absent → the client's position as it stands. */
    supportCost?: SupportCostInput | null;
    inflationAssumptionPct?: number;
}

/**
 * A care date far enough out that no projected period can contain it.
 *
 * Not "no date at all": a null `careStartDate` means "already in care as the run opens", which is
 * the legacy reading and the opposite of what this module models. The date has to be present and
 * unreachable.
 */
const NEVER = '2199-01-01';

/** The fee schedule for a client who never enters residential care — every rate nil. */
const noCareCosts = (scenarioLivingExpenses: number) => ({
    totalDailyCost: 0,
    dailyAccommodationPayment: 0,
    basicDailyFee: 0,
    dailyNCCC: 0,
    dailyHSC: 0,
    dailyExtraServiceFee: 0,
    scenarioLivingExpenses,
});

/**
 * The client's living expenses for a run, with any support cost folded in.
 *
 * Folded rather than modelled separately because the engine has one recurring-expense channel
 * (`expenses.livingExpenses`); `capitalExpenses` are one-offs. The module keeps the two APART on
 * screen so the client can always see what the support is costing them — the arithmetic is joined,
 * the presentation is not.
 */
export function livingExpensesWithSupport(factFind: DraftFactFind, supportCost?: SupportCostInput | null): number {
    const base = parseFloat(String(factFind?.expenses?.livingExpenses ?? '')) || 0;
    return base + Math.max(0, supportCost?.annual || 0);
}

/**
 * Project the client forward with no care event.
 *
 * Passing `supportCost` runs the WITH-cost world; omitting it runs their position as it stands. The
 * two are otherwise identical, so any difference between them is the support cost and nothing else.
 */
export async function runCashflowProjections(
    factFind: DraftFactFind,
    options: RunOptions = {},
): Promise<CashflowYear[]> {
    const legacyYears = options.legacyYears || 10;
    const scenarioLivingExpenses = livingExpensesWithSupport(factFind, options.supportCost);
    const simulated: DraftFactFind = {
        ...factFind,
        expenses: { ...(factFind.expenses || {}), livingExpenses: scenarioLivingExpenses },
    } as DraftFactFind;

    const result = await calculationApi.runProjections(
        simulated,
        {
            scenarioLivingExpenses,
            careStartDate: NEVER,
            ...(options.inflationAssumptionPct != null ? { inflationAssumptionPct: options.inflationAssumptionPct } : {}),
        } as never,
        noCareCosts(scenarioLivingExpenses) as never,
        legacyYears,
    );
    return (result || []) as CashflowYear[];
}

/** The headline figures a client reads first, taken from the first projected year and the last. */
export interface PositionSummary {
    agePension: number;
    cashFlow: number;
    netWorth: number;
    estate: number;
    /** The first projected year the plan cannot be funded, or null if it never happens. */
    shortfallYear: number | null;
    livingExpenses: number;
    totalIncome: number;
}

export function summarisePosition(years: CashflowYear[]): PositionSummary | null {
    if (!years?.length) return null;
    const first = years[0];
    const last = years[years.length - 1];
    /*
     * The runway. `unfundedShortfall` is the engine's own signal that a year's plan could not be
     * paid for out of income plus drawdown — the honest answer to "how long does this last?", and
     * far more useful to a client than a net-worth line that quietly goes to zero.
     */
    const shortfall = years.find((y) => (Number(y.unfundedShortfall) || 0) > 0);
    return {
        agePension: Number(first.socialSecurity) || 0,
        cashFlow: Number(first.cashFlow) || 0,
        netWorth: Number(first.netWorth) || 0,
        estate: estateOf(last) ?? 0,
        shortfallYear: shortfall ? Number(shortfall.year) : null,
        livingExpenses: Number(first.livingExpenses) || 0,
        totalIncome:
            (Number(first.investmentIncome) || 0) +
            (Number(first.abpIncome) || 0) +
            (Number(first.rentalIncome) || 0) +
            (Number(first.socialSecurity) || 0),
    };
}

/** What a support cost does — the same projection run twice, differing only in that cost. */
export interface SupportCostImpact {
    without: PositionSummary;
    with: PositionSummary;
    /** Negative: the estate is smaller for having paid the cost. */
    estateChange: number;
    cashFlowChange: number;
    /** Age Pension is means tested on assets and income, not on what you spend — expected to be nil. */
    pensionChange: number;
}

export async function compareWithAndWithoutSupport(
    factFind: DraftFactFind,
    supportCost: SupportCostInput,
    legacyYears = 10,
): Promise<SupportCostImpact | null> {
    const [without, withCost] = await Promise.all([
        runCashflowProjections(factFind, { legacyYears }),
        runCashflowProjections(factFind, { legacyYears, supportCost }),
    ]);
    const a = summarisePosition(without);
    const b = summarisePosition(withCost);
    if (!a || !b) return null;
    return {
        without: a,
        with: b,
        estateChange: b.estate - a.estate,
        cashFlowChange: b.cashFlow - a.cashFlow,
        pensionChange: b.agePension - a.agePension,
    };
}

/**
 * One evaluated candidate, as the server returns it.
 *
 * Mirrors CashflowPensionResult in server/.../suggestionEngine/cashflowPension.ts — the frontend
 * cannot import server code, so this is the boundary contract. `versusBaseline` is the figure the
 * cards actually show: everything is stated as a change against doing nothing.
 */
export interface CashflowPensionResult {
    id: string;
    name: string;
    description: string;
    changeNothing: boolean;
    projections: CashflowYear[];
    snapshot: {
        pension: number;
        cashflow: number;
        estate: number;
        shortfallYear: number | null;
    };
    versusBaseline?: {
        pension: number;
        cashflow: number;
        estate: number;
    };
    /**
     * The terms the annuity was built on, where this option buys one — owner, residual capital,
     * reversionary, indexation. Reported by the engine rather than restated by the screen, so the
     * disclosure and the arithmetic cannot drift apart.
     */
    annuityTerms?: Record<string, unknown> | null;

    error?: string;
}

/**
 * What the client has chosen to move, in Manual Control.
 *
 * THE DIFFERENCE FROM EVERY OTHER MODULE'S ALLOCATOR, and it matters. Elsewhere Manual Control
 * deploys PROCEEDS — a lump sum that a sale has just created, sitting outside the balance sheet
 * waiting to be placed. Here there is no sale and no new money. The client is REARRANGING what they
 * already own, so the pool is their existing liquid assets and every dollar moved has to come out of
 * something they currently hold.
 *
 * That is why this is its own shape rather than ProceedsDeploymentFields: reusing an allocator built
 * around "here is $400,000, where does it go?" would have modelled money appearing from nowhere.
 */
export interface ManualAllocation {
    /** Into a funeral bond — exempt up to the published limit per person. */
    funeralBond: number;
    /** Given away — outside the free areas it is still counted for five years. */
    gift: number;
    /** Into a lifetime annuity — 60% of the purchase price is assessed. */
    annuity: number;
    /** Off a debt — reduces what is owed and frees the repayments. */
    debtRepayment: number;
    /**
     * Into super as an after-tax contribution (cashflow rebuild, 21 Oct 2026 — so a tested plan that uses it
     * can be built on by hand). Split between the two of a couple as the tested plans split it; the engine
     * caps each person at what the rules allow and returns the rest to the bank.
     */
    superContribution?: number;
    /** Into an investment bond — counted and deemed like the cash it replaces; it changes the tax. */
    investmentBond?: number;
}

export const EMPTY_ALLOCATION: ManualAllocation = { funeralBond: 0, gift: 0, annuity: 0, debtRepayment: 0 };

/** An allocation's amount for one lever, absent read as nothing. */
export const allocationOf = (allocation: ManualAllocation, key: keyof ManualAllocation): number => Math.max(0, Number(allocation[key]) || 0);

/**
 * How the client wants the annuity STRUCTURED, kept apart from how much goes into it.
 *
 * OWNER-DIRECTED, 2026-09-01: every other module lets a client set the owner, the residual capital,
 * whether it is reversionary and whether it is indexed — this one hard-coded all four and offered a
 * slider. The amount stays in `ManualAllocation` because `allocationTotal` sums that map and a
 * non-numeric field in it would corrupt the total; the terms live here instead.
 */
export interface ManualAnnuityTerms {
    /** 0 to 1. Nil pays the most income and leaves nothing; 1 returns the purchase price. */
    residualCapital: number;
    owner: 'client' | 'partner' | 'joint';
    reversionary: boolean;
    indexation: boolean;
    /** A real provider quote, replacing the indicative rate. */
    incomeOverride: number | null;
    /**
     * 'guaranteed' is the guaranteed income with life cover that returns the full amount at any time, for a
     * person who may buy it (in or entering care, or receiving support at home); otherwise a lifetime annuity.
     * Absent on plans saved before 21 Oct 2026: a lifetime annuity.
     */
    annuityType?: 'lifetime' | 'guaranteed';
}

export const DEFAULT_ANNUITY_TERMS: ManualAnnuityTerms = {
    /*
     * Capital preserved, like every other module's Manual Control — see
     * DEFAULT_NEW_ANNUITY_RESIDUAL in SupportAtHomeAnnuityConfigurator.tsx for why the default is
     * the option that does NOT commit the client's money permanently. The means test is identical
     * either way, so the default costs nothing and the client still chooses.
     */
    residualCapital: 1,
    owner: 'client',
    reversionary: false,
    indexation: false,
    incomeOverride: null,
};

/** Every liquid pot the client could actually move money out of. */
export interface LiquidSource {
    type: string;
    id: string;
    description: string;
    value: number;
}

export function liquidSourcesOf(factFind: DraftFactFind): LiquidSource[] {
    const assets: any = factFind?.assets || {};
    const collect = (type: string) =>
        (assets[type] || [])
            .filter((row: any) => row?.id && (parseFloat(String(row?.value ?? '')) || 0) > 0)
            .map((row: any) => ({
                type,
                id: String(row.id),
                // Display name: a couple's two accounts of one name gain their owners' names (assetDisplayName).
                description: assetDisplayName(row, factFind, type),
                value: parseFloat(String(row.value)) || 0,
            }));
    return [...collect('bankAccounts'), ...collect('termDeposits'), ...collect('shares')];
}

export function totalLiquid(factFind: DraftFactFind): number {
    return liquidSourcesOf(factFind).reduce((sum, s) => sum + s.value, 0);
}

export function allocationTotal(allocation: ManualAllocation): number {
    return allocationOf(allocation, 'funeralBond') + allocationOf(allocation, 'gift')
        + allocationOf(allocation, 'annuity') + allocationOf(allocation, 'debtRepayment')
        + allocationOf(allocation, 'superContribution') + allocationOf(allocation, 'investmentBond');
}

/**
 * Turn an allocation into the funding plan the engine's levers require, drawing bank first.
 *
 * Bank before shares because selling shares can crystallise capital gains tax that this module does
 * not model — proposing it silently would understate the cost of the move.
 */
export function fundingPlanFor(sources: LiquidSource[], amount: number): Array<{ type: string; id: string; amountToTake: number }> {
    const plan: Array<{ type: string; id: string; amountToTake: number }> = [];
    let remaining = Math.max(0, amount);
    for (const source of sources) {
        if (remaining <= 0) break;
        const take = Math.min(source.value, remaining);
        if (take > 0) plan.push({ type: source.type, id: source.id, amountToTake: take });
        remaining -= take;
    }
    return plan;
}

/**
 * REOPENING A SAVED PLAN (review, 18 Oct 2026: "saved plans never reopen", though the page said they
 * did). Home hands the saved record over in sessionStorage under this key — the same hand-off
 * Support at Home uses ('careCompanionReopenHomeCareStrategy') — and the page reads it once on
 * opening, then clears it.
 */
export const CASHFLOW_REOPEN_KEY = 'careCompanionReopenCashflowStrategy';

/** What a saved cashflow plan carries back into the page. */
export interface ReopenedCashflowPlan {
    name: string;
    mode: 'manual' | 'optimised';
    allocation: ManualAllocation | null;
    annuityTerms: ManualAnnuityTerms | null;
    engineOptionId: string | null;
    supportCostAnnual: number;
    supportCostLabel: string | null;
    legacyYears: number | null;
    /** A provider quote the engine's annuity was ranked on, where one was entered. */
    annuityQuote: number | null;
    /** What that quote was for: the chosen option's guaranteed income, its type and amount (annuityQuote.ts). */
    annuityQuoteFor: { shape: AnnuityQuoteShape; amount: number } | null;
    /** The card chosen, as the plan page shows it (cpChosenPlan.ts); null on a plan saved before 21 Oct 2026. */
    card?: unknown;
    /** When it was chosen, and whether it is the chosen plan. */
    createdAt?: string | null;
    chosen?: boolean;
    /** The saved record's id, so the plan page can mark its advice document as prepared. */
    id?: string | null;
    /** When the advice document was last prepared for it. */
    soaPreparedAt?: string | null;
}

const num = (v: unknown): number | null => {
    const x = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
    return Number.isFinite(x) ? x : null;
};

/** The saved record (as CashflowPensionPage saves it) read back into the page's own state. */
export function reopenedPlanFrom(saved: unknown): ReopenedCashflowPlan | null {
    if (!saved || typeof saved !== 'object') return null;
    const s = saved as Record<string, any>;
    if (s.type && s.type !== 'cashflow_pension') return null;
    const inputs = (s.inputs || {}) as Record<string, any>;
    const mode = (inputs.source || s.source) === 'manual' ? 'manual' : 'optimised';
    const alloc = inputs.allocation && typeof inputs.allocation === 'object'
        ? { ...EMPTY_ALLOCATION, ...Object.fromEntries(Object.entries(inputs.allocation).map(([k, v]) => [k, num(v) ?? 0])) } as ManualAllocation
        : null;
    const quote = num(inputs.candidate?.annuity?.incomeOverride);
    return {
        name: String(s.name || ''),
        mode,
        allocation: mode === 'manual' ? alloc : null,
        annuityTerms: inputs.annuityTerms && typeof inputs.annuityTerms === 'object' ? { ...DEFAULT_ANNUITY_TERMS, ...inputs.annuityTerms } : null,
        engineOptionId: mode === 'manual' ? null : (inputs.engineOptionId ?? inputs.engineStrategyId ?? null),
        supportCostAnnual: Math.max(0, num(inputs.supportCostAnnual ?? inputs.planContext?.supportCostAnnual) ?? 0),
        supportCostLabel: inputs.supportCostLabel ? String(inputs.supportCostLabel) : null,
        legacyYears: num(inputs.legacyYears ?? inputs.planContext?.legacyYears),
        annuityQuote: mode === 'manual' ? null : (quote && quote > 0 ? quote : null),
        // The quote was entered on the chosen option, so it is for that option's type and amount.
        annuityQuoteFor: mode === 'manual' || !(quote && quote > 0) || !(num(inputs.candidate?.annuity?.purchaseAmount)! > 0)
            ? null
            : {
                shape: annuityQuoteShapeOf(inputs.candidate.annuity.annuityType, Math.round((num(inputs.candidate.annuity.residualCapital) ?? 0) * 100)),
                amount: num(inputs.candidate.annuity.purchaseAmount)!,
            },
        card: inputs.card && typeof inputs.card === 'object' ? inputs.card : null,
        createdAt: s.createdAt ? String(s.createdAt) : null,
        chosen: s.chosen === true,
        id: s.id != null ? String(s.id) : null,
        soaPreparedAt: s.soaPreparedAt ? String(s.soaPreparedAt) : null,
    };
}

/** Reads the hand-off without clearing it (the page clears it in an effect, so a double render cannot lose it). */
export function peekReopenedCashflowPlan(): ReopenedCashflowPlan | null {
    try {
        if (typeof window === 'undefined') return null;
        const raw = sessionStorage.getItem(CASHFLOW_REOPEN_KEY);
        return raw ? reopenedPlanFrom(JSON.parse(raw)) : null;
    } catch {
        return null;
    }
}

/** One debt Manual Control can repay. Reverse mortgages are left out, as every other payoff does. */
export interface CashflowPayableDebt {
    /** The shared payoff key, "<category>::<liabilityId>" (support/debtPayoff.ts). */
    key: string;
    description: string;
    balance: number;
    interestRate: number;
}

export function payableDebtsForCashflow(factFind: DraftFactFind | null | undefined): CashflowPayableDebt[] {
    const debts: CashflowPayableDebt[] = [];
    const liabilities: Record<string, any> = (factFind as any)?.liabilities || {};
    for (const [category, list] of Object.entries(liabilities)) {
        if (!Array.isArray(list)) continue;
        for (const liability of list) {
            if (!liability?.id || liability.type === 'reverse_mortgage') continue;
            const balance = parseFloat(String(liability.balance ?? '')) || 0;
            if (balance <= 0) continue;
            // A card paid off in full each month is a monthly bill, not a debt to repay (strategy/debtsToTest.ts).
            if (isCreditCard(liability, category) && cardPaymentStyleOf(liability) === 'full') continue;
            debts.push({
                key: debtPayoffKey(category, liability.id),
                description: String(liability.description || 'Debt'),
                balance,
                interestRate: liabilityInterestRate(liability, category),
            });
        }
    }
    return debts;
}

/** What each source still holds once a funding plan has drawn on it. */
function remainingAfter(sources: LiquidSource[], plan: Array<{ type: string; id: string; amountToTake: number }>): LiquidSource[] {
    return sources
        .map((s) => {
            const taken = plan.filter((p) => p.type === s.type && p.id === s.id).reduce((sum, p) => sum + p.amountToTake, 0);
            return { ...s, value: Math.max(0, s.value - taken) };
        })
        .filter((s) => s.value > 0);
}

/**
 * The repayment as a shared `debtPayoff`: dearest debt first (the interest saved is largest there),
 * each clamped to what is owed, funded bank first from what the other levers left. Null when there
 * is nothing to repay or nothing left to repay it with.
 */
export function manualDebtPayoff(
    factFind: DraftFactFind,
    amount: number,
    sources: LiquidSource[],
): { payments: Record<string, number>; funding: Record<string, number>; repaymentBasis: 'keep_repayment' } | null {
    const debts = payableDebtsForCashflow(factFind).sort((a, b) => b.interestRate - a.interestRate);
    const available = sources.reduce((sum, s) => sum + s.value, 0);
    let remaining = Math.min(Math.max(0, Number(amount) || 0), debts.reduce((sum, d) => sum + d.balance, 0), available);
    if (remaining <= 0) return null;
    const total = remaining;

    const payments: Record<string, number> = {};
    for (const debt of debts) {
        if (remaining <= 0) break;
        const pay = Math.min(debt.balance, remaining);
        payments[debt.key] = pay;
        remaining -= pay;
    }
    const funding: Record<string, number> = {};
    for (const source of fundingPlanFor(sources, total)) {
        funding[`${source.type}-${source.id}`] = source.amountToTake;
    }
    return { payments, funding, repaymentBasis: 'keep_repayment' };
}

/**
 * A manual allocation as the SAME candidate shape the engine ranks (the page evaluates it with the
 * engine's own call). Kept here so the page and the Action Plan's after-the-plan forms build one
 * plan from one allocation.
 */
export function manualCandidateFrom(allocation: ManualAllocation, annuityTerms: ManualAnnuityTerms, sources: LiquidSource[], factFind?: DraftFactFind | null) {
    /*
     * "OFF YOUR DEBT" USED TO REPAY NOTHING (found 18 Oct 2026). The amount was added to the funding
     * plan, but no `debtPayoff` was built, and the engine liquidates only the slice each lever takes —
     * so the debt, its interest and its repayments carried on as if nothing had been entered.
     *
     * Now the levers' own funding (gift, bond, annuity — the slices the engine takes in that order)
     * is drawn first, and the debt repayment is funded from what the same sources have left, as a
     * `debtPayoff` in the shared shape (support/debtPayoff.ts): payments keyed "<category>::<id>"
     * against the liabilities on the file, funding keyed "<assetType>-<assetId>". The engine applies
     * it at the start of the projection: the assets are drawn, the balance falls, and the repayment
     * plugin stops servicing a debt that reaches zero.
     */
    const superAmount = allocationOf(allocation, 'superContribution');
    const bondAmount = allocationOf(allocation, 'investmentBond');
    /*
     * After-tax super is split between a couple the way the tested plans split it (the person whose super
     * Centrelink does not yet count first, else whoever has room), so a tested plan built on by hand runs the
     * same contribution. Single, or no file: all of it to the client.
     */
    const superSplit = superAmount > 0
        ? (factFind ? allocateNonConcessionalContribution(factFind as unknown as FactFind, superAmount) : { client: superAmount, partner: 0, preferred: null })
        : null;
    // Only what super can take is drawn for it; the rest stays in the bank, as the engine would leave it.
    const superFunded = superSplit ? superSplit.client + superSplit.partner : 0;
    const leverTotal = Math.max(0, allocation.funeralBond) + Math.max(0, allocation.gift) + Math.max(0, allocation.annuity) + superFunded + bondAmount;
    const plan = fundingPlanFor(sources, leverTotal);
    const debtPayoff = factFind ? manualDebtPayoff(factFind, allocation.debtRepayment, remainingAfter(sources, plan)) : null;
    return {
        ...(debtPayoff ? { debtPayoff } : {}),
        id: 'cp-manual',
        name: 'Your plan',
        description: 'The allocation you have entered above.',
        ...(allocation.funeralBond > 0 ? { funeralBondPurchase: allocation.funeralBond } : {}),
        ...(allocation.gift > 0 ? { giftingAmount: allocation.gift } : {}),
        ...(superSplit && superSplit.client + superSplit.partner > 0
            ? { superContribution: { client: superSplit.client, partner: superSplit.partner, commenceIncomeStream: superSplit.preferred == null } }
            : {}),
        ...(bondAmount > 0 ? { investmentBond: bondAmount } : {}),
        ...(allocation.annuity > 0
            /*
             * `ownership`, NOT `owner`. The engine's annuityTermsFor reads `ownership`; this
             * passed `owner`, which it read straight past — so a partner- or jointly-owned
             * annuity would have been built in the client's name whatever the client chose
             * (2026-09-01). It accepts both spellings now, and this sends the one the rest of
             * the engines send.
             */
            ? {
                  annuity: {
                      purchaseAmount: allocation.annuity,
                      annuityType: annuityTerms.annuityType === 'guaranteed' ? 'guaranteed' : 'lifetime',
                      ownership: annuityTerms.owner,
                      residualCapital: annuityTerms.residualCapital,
                      reversionary: annuityTerms.reversionary,
                      indexation: annuityTerms.indexation,
                      incomeOverride: annuityTerms.incomeOverride,
                  },
              }
            : {}),
        fundingSources: plan,
    };
}

/** The cashflow module's own answers (step 3's questions), and nothing else. */
export interface CashflowGoals {
    cpEmergencyReserve?: number;
    cpLevers?: string;
}
const CASHFLOW_GOAL_KEYS = ['cpEmergencyReserve', 'cpLevers'] as const;

/**
 * THE PAGE'S THIRD WRITER (cashflow rebuild, 21 Oct 2026): step 3's answers — the money kept for emergencies
 * and the levers the client would consider — and only those keys. The page still never holds setFactFind:
 * the support cost stays the client's own figure and is never written.
 */
export function cashflowGoalsSetter(setFactFind: ((updater: (prev: DraftFactFind) => DraftFactFind) => void) | null | undefined): ((patch: CashflowGoals) => void) | undefined {
    if (!setFactFind) return undefined;
    return (patch: CashflowGoals) => setFactFind((prev) => {
        const goals = { ...((prev?.goals || {}) as Record<string, unknown>) };
        for (const key of CASHFLOW_GOAL_KEYS) {
            if (key in (patch || {})) goals[key] = (patch as Record<string, unknown>)[key];
        }
        return { ...prev, goals } as DraftFactFind;
    });
}

/** The goals keys the guaranteed-income question writes (GuaranteedIncomeQuestions), and only those. */
const GUARANTEED_INCOME_GOAL_KEYS = ['annuityAppetite', 'annuityResidual', 'annuityShareCapPct', 'annuityShareCapAmount', 'annuityAnsweredOn'] as const;

/**
 * THE PAGE'S SECOND WRITER, FOR THE CLIENT'S GUARANTEED-INCOME ANSWERS ALONE (owner, 21 Oct 2026). This page
 * never holds setFactFind (the support cost is the client's own figure and is never written), so App gives
 * it a writer that can touch only these goals keys: whatever else a patch carries is dropped.
 */
export function guaranteedIncomeGoalsSetter(setFactFind: ((updater: (prev: DraftFactFind) => DraftFactFind) => void) | null | undefined): ((patch: GuaranteedIncomeGoals) => void) | undefined {
    if (!setFactFind) return undefined;
    return (patch: GuaranteedIncomeGoals) => setFactFind((prev) => {
        const goals = { ...((prev?.goals || {}) as Record<string, unknown>) };
        for (const key of GUARANTEED_INCOME_GOAL_KEYS) {
            if (key in (patch || {})) goals[key] = (patch as Record<string, unknown>)[key];
        }
        return { ...prev, goals } as DraftFactFind;
    });
}
